import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import { Link } from "react-router-dom";
import ReactPaginate from 'react-paginate';
import { formatTo12Hour } from '../../utils/formatTo12Hour';
import { getRoleBasedRoute } from '../../utils/routeUtils';
import { AuthContext } from "../../contexts/AuthContext";

const CheckAttendancePage = () => {
    const [file, setFile] = useState(null);
    const [monthlyRecords, setMonthlyRecords] = useState(null);
    
    // Initialize with current year and month
    const getCurrentYearMonth = () => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = String(currentDate.getMonth() + 1).padStart(2, "0");
        return `${currentYear}-${currentMonth}`;
    };
    
    const [yearDay, setYearDay] = useState(getCurrentYearMonth());
    const [loading, setLoading] = useState(false);
    const [loadingEmployee, setLoadingEmployee] = useState(false);
    const [error, setError] = useState("")
    const [employeeId, setEmployeeId] = useState("");
    const [employeeRecords, setEmployeeRecords] = useState(null);
    const token = getToken()
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        perPage: 10,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const { roles } = useContext(AuthContext);
    const [isFiltering, setIsFiltering] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [activeFilters, setActiveFilters] = useState({});
    const [filteredData, setFilteredData] = useState(employeeRecords);
    const [tempFilters, setTempFilters] = useState({});

    const toggleFilterMenu = () => {
        setShowFilterMenu(!showFilterMenu);
    };

    const hasTempFilters = Object.values(tempFilters).some((val) => val !== "");

    const filterableColumns = [
        // { field: "created_at", label: "Date" },
        { field: "employee_id", label: "EMP ID" },
        { field: "in_time", label: "In Time" },
        { field: "out_time", label: "Out Time" },
        { field: "total_hours", label: "Total Hours" },
        { field: "da_type", label: "Attendance Status" },
        { field: "portion", label: "Portion" },
    ];

    // Convert "20-05-2025" to "2025-05-20"
    function formatToInputDate(value) {
        if (!value) return "";
        const [day, month, year] = value.split("-");
        if (!day || !month || !year) return "";
        return `${year}-${month}-${day}`;
    }

    // Convert "2025-05-20" to "20-05-2025"
    function formatToDisplayDate(value) {
        if (!value) return "";
        const [year, month, day] = value.split("-");
        if (!day || !month || !year) return "";
        return `${day}-${month}-${year}`;
    }

    const clearFilters = () => {
        setActiveFilters({});
        setSearchQuery("");
        setTempFilters({});
    };

    const clearFilterByKey = (key) => {
        const updatedFilters = { ...activeFilters };
        delete updatedFilters[key];
        setActiveFilters(updatedFilters);
    };

    useEffect(() => {
    if (showFilterMenu) {
        setTempFilters(activeFilters);
    }
    }, [showFilterMenu]);

    // Updated useEffect to fetch data when component mounts or yearDay changes
    useEffect(() => {
        if (token && yearDay) {
            fetchMonthlyAttendance();
        }
    }, [token, yearDay]);

    useEffect(() => {
        setFilteredData(monthlyRecords);
    }, [monthlyRecords]);

    // Apply filters and search when activeFilters or searchQuery changes
    useEffect(() => {
        if (!monthlyRecords) return;
        
        let filtered = [...monthlyRecords];
        
        // Apply search query
        if (searchQuery.trim()) {
            filtered = filtered.filter(record => 
                Object.values(record).some(value => 
                    value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
                )
            );
        }
        
        // Apply active filters
        Object.entries(activeFilters).forEach(([field, filterValue]) => {
            if (filterValue && filterValue.trim()) {
                filtered = filtered.filter(record => {
                    const recordValue = record[field];
                    if (!recordValue) return false;
                    return recordValue.toString().toLowerCase().includes(filterValue.toLowerCase());
                });
            }
        });
        
        setFilteredData(filtered);
        setIsFiltering(searchQuery.trim() !== "" || Object.values(activeFilters).some(val => val && val.trim()));
    }, [monthlyRecords, activeFilters, searchQuery]);

    const getRoleBasedRouteLocal = (route) => {
        return getRoleBasedRoute(route, roles);
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleYearChange = (e) => {
    const newYear = e.target.value;
    const [, month] = yearDay.split("-");
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    // adjust month if user picks current year but month is > current month
    const adjustedMonth =
        parseInt(newYear) === currentYear && parseInt(month) > currentMonth
        ? String(currentMonth).padStart(2, "0")
        : month;

    setYearDay(`${newYear}-${adjustedMonth}`);
    };

    // Handler to update month
    const handleDayChange = (e) => {
    const newMonth = e.target.value;
    const [year] = yearDay.split("-");
    setYearDay(`${year}-${newMonth}`);
    };

    const fetchMonthlyAttendance = async (page = 1) => {
    setLoadingEmployee(true);
    setError(null);
    
    try {
        const response = await axios.post(
        `https://hrmsnew.prepstripe.com/api/attendance/monthly?page=${page}`,
        { period: yearDay },
        {
            headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            },
        }
        );

        setMonthlyRecords(response.data.data.data); 
        
        setPagination({
        currentPage: response.data.data?.current_page || 1,
        totalPages: response.data.data?.last_page || 1,
        totalItems: response.data.data?.total || 0,
        perPage: response.data.data?.per_page || 30,
        });
        
    } catch (error) {
        console.error("Error fetching monthly attendance:", error);
        setError("Failed to fetch monthly attendance. Please try again.");
    } finally {
        setLoadingEmployee(false);
    }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
          setCurrentPage(newPage);
          fetchMonthlyAttendance(newPage);
        }
    };
  
      
  return (
    // mobile responsive
    <div className="px-4 py-2 bg-white dark:bg-gray-900 min-h-screen font-poppins">
        {/* Breadcrumb */}
        <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse text-sm">
            <li>
                <div className="flex items-center">
                <Link
                    to={getRoleBasedRouteLocal("/admin/attendance_management")}
                    className="text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
                >
                    Attendance Management
                </Link>
                </div>
            </li>
            <li aria-current="page">
                <div className="flex items-center">
                <svg className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1" aria-hidden="true" fill="none" viewBox="0 0 6 10">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
                </svg>
                <span className="ms-1 font-medium text-gray-500 md:ms-2">Check Attendance</span>
                </div>
            </li>
            </ol>
        </nav>

        {/* Fetch Employee Attendance */}
        <div className="mb-6 bg-white dark:bg-gray-950 mt-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4 dark:text-white">Get Monthly Attendance</h2>
            
            <div className="flex justify-between">
                <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">  
                    {/* Year and Day Dropdowns */}
                    <div className="flex gap-2 w-full sm:w-auto">
                        <select
                            value={yearDay.split("-")[0]}
                            onChange={handleYearChange}
                            className="border dark:bg-black dark:border-gray-700 dark:text-white p-2 rounded w-full sm:w-auto"
                        >
                            {Array.from({ length: 11 }, (_, i) => {
                            const y = new Date().getFullYear() - 5 + i;
                            return (
                                <option key={y} value={y}>
                                {y}
                                </option>
                            );
                            })}
                        </select>

                        <select
                            value={yearDay.split("-")[1]}
                            onChange={handleDayChange}
                            className="border dark:bg-black dark:border-gray-700 dark:text-white p-2 rounded w-full sm:w-auto"
                        >
                            {Array.from(
                            {
                                length:
                                parseInt(yearDay.split("-")[0]) === new Date().getFullYear()
                                    ? new Date().getMonth() + 1 // till current month
                                    : 12, // full year
                            },
                            (_, i) => {
                                const m = String(i + 1).padStart(2, "0"); // "01", "02", ...
                                return (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                                );
                            }
                            )}
                        </select>
                    </div>

                    {/* Search Input */}
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border dark:text-white dark:bg-black dark:border-gray-700 dark:focus:outline-none p-2 rounded w-full sm:w-48"
                    />
                </div>  

                {/* filter button */}
                <div className="flex justify-between items-center gap-2 w-auto">
                    <div className="relative ">
                        <button 
                            onClick={toggleFilterMenu}
                            className="w-full p-2 border border-[#F4F7FF] dark:bg-gray-900 dark:text-white dark:border-gray-700 rounded-md flex gap-1 items-center bg-[#F4F7FF] hover:bg-gray-200"
                            >
                            <svg width="18" height="17" viewBox="0 0 13 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.98734 13.5608C7.85034 13.5608 7.71598 13.5252 7.59884 13.4581L5.09834 12.0415C4.817 11.8812 4.58412 11.6552 4.42215 11.3852C4.26017 11.1153 4.17457 10.8104 4.17359 10.5001V7.29493C4.17408 7.06871 4.0996 6.84803 3.96059 6.6638L0.469345 2.02988C0.387822 1.92291 0.339174 1.79674 0.328769 1.66531C0.318364 1.53387 0.346605 1.40228 0.410378 1.28504C0.474151 1.16781 0.570975 1.06949 0.690169 1.00093C0.809362 0.932381 0.94629 0.896259 1.08584 0.896552H11.8333C11.973 0.896002 12.1101 0.931967 12.2295 1.00047C12.3488 1.06896 12.4458 1.16732 12.5096 1.28465C12.5734 1.40198 12.6016 1.5337 12.5911 1.66523C12.5805 1.79677 12.5317 1.92297 12.4498 2.02988L8.95859 6.6638C8.81933 6.84794 8.74458 7.06863 8.74484 7.29493V12.844C8.74465 13.0338 8.66482 13.2158 8.52285 13.3502C8.38088 13.4845 8.18832 13.5603 7.98734 13.5608ZM1.08584 1.60843L4.57109 6.25297C4.80208 6.55645 4.92547 6.92118 4.92359 7.29493V10.4994C4.92387 10.6874 4.9756 10.8722 5.07376 11.0358C5.17192 11.1994 5.31319 11.3362 5.48384 11.433L7.98434 12.8497L7.99484 7.29422C7.99319 6.92039 8.11685 6.55566 8.34809 6.25226L11.8386 1.61834L1.08584 1.60843Z" fill="currentColor"/>
                            </svg>
                            <span className="ml-1">Filter</span>
                        </button>
                        
                        {showFilterMenu && (
                        <div className="absolute z-10 mt-2 md:right-0 right-auto left-0 md:left-auto bg-white dark:bg-black dark:border-gray-700 border border-gray-200 rounded-md shadow-lg p-4 w-64">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="font-medium dark:text-white">Filters</h3>
                                
                                {hasTempFilters && (
                                    <button
                                        onClick={() => {
                                        setActiveFilters(tempFilters);
                                        setShowFilterMenu(false);
                                        }}
                                        className=" bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 text-sm"
                                    >
                                        Apply Filters
                                    </button>
                                )}
                                
                            </div>

                            <div className="space-y-3">
                                {filterableColumns.map((column) => (
                                    <div key={column.field} className="space-y-1">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-white">
                                        {column.label}
                                    </label>

                                    {column.field === "approval_status" ? (
                                        <select
                                        value={tempFilters[column.field] || ""}
                                        // onChange={(e) => handleFilterChange(column.field, e.target.value)}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setTempFilters(prev => ({ ...prev, [column.field]: value }));
                                        }}
                                        className="w-full p-2 dark:bg-gray-950 dark:text-white dark:border-gray-700 text-sm border border-gray-300 rounded-md"
                                        >
                                        <option value="">All</option>
                                        <option value="approved">Approved</option>
                                        <option value="pending">Pending</option>
                                        </select>
                                    ) : column.field === "created_at" ? (
                                        <input
                                        type="date"
                                        value={
                                            tempFilters[column.field]
                                            ? formatToInputDate(tempFilters[column.field])
                                            : ""
                                        }

                                        onChange={(e) => {
                                            const rawValue = e.target.value;
                                            const formattedValue = formatToDisplayDate(rawValue);
                                            
                                            setTempFilters(prev => ({
                                                ...prev,
                                                [column.field]: formattedValue
                                            }));

                                        }}

                                        className="w-full dark:bg-gray-950 dark:border-gray-700 dark:text-white p-2 text-sm border border-gray-300 rounded-md"
                                        />
                                    ) : (
                                        <input
                                        type="text"
                                        value={tempFilters[column.field] || ""}
                                        // onChange={(e) => handleFilterChange(column.field, e.target.value)}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setTempFilters(prev => ({ ...prev, [column.field]: value }));
                                        }}
                                        placeholder={`Filter by ${column.label.toLowerCase()}`}
                                        className="w-full dark:bg-gray-950 dark:border-gray-700 dark:text-white  p-2 text-sm border border-gray-300 rounded-md"
                                        />
                                    )}
                                    </div>
                                ))}
                            </div>
                            
                            {isFiltering && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-sm text-gray-600 dark:text-white">
                                Showing {filteredData?.length || 0} of {monthlyRecords?.length || 0} results
                                </p>
                            </div>
                            )}
                        </div>
                        )}
                    </div>

                    {isFiltering && (
                    <div className="flex flex-wrap items-center justify-between w-full">
                        <div className="flex flex-wrap gap-2">
                        {Object.entries(activeFilters).map(([key, value]) => (
                            value && (
                            <div
                                key={key}
                                className="flex items-center gap-2 px-3 py-1.5 border border-blue-500 bg-blue-50 dark:bg-gray-900 text-blue-600 rounded-full text-sm shadow-sm"
                            >
                                <span className="capitalize dark:text-white">{key.replace('_', ' ')}: {value}</span>
                                <button
                                onClick={() => clearFilterByKey(key)}
                                className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-blue-100 focus:outline-none"
                                aria-label={`Clear ${key}`}
                                >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                </button>
                            </div>
                            )
                        ))}
                        </div>

                        <button
                        onClick={clearFilters}
                        className="ml-4 text-sm text-red-500 hover:underline whitespace-nowrap"
                        >
                        Clear All
                        </button>
                    </div>
                    )}

                </div>


            </div>

            {/* Attendance Table */}
            <div className="overflow-x-auto mt-2 bg-white dark:bg-gray-900 shadow-md">
                <table className="min-w-full table-auto md:table-fixed text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
                        <tr>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Employee ID</th>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Date</th>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">In Time</th>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Out Time</th>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Total Hours</th>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Attendance Status</th>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Portion</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                    {loading || loadingEmployee ? (
                        <tr className="border-b hover:bg-gray-100 dark:hover:bg-gray-800">
                        <td colSpan="7" className="py-2 px-4 text-xs md:text-sm whitespace-nowrap">Loading...</td>
                        </tr>
                    ) : (isFiltering ? filteredData : monthlyRecords)?.length > 0 ? (
                        (isFiltering ? filteredData : monthlyRecords).map((record, index) => (
                        <tr key={index} className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-950 dark:hover:text-white">
                            <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">{record.employee_id}</td>
                            <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">{record.attendance_date}</td>
                            <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">
                            {record.in_time ? formatTo12Hour(record.in_time) : '-'}
                            </td>
                            <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">
                            {record.out_time ? formatTo12Hour(record.out_time) : '-'}
                            </td>
                            <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">{record.total_hours}</td>
                            <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">{record.da_type}</td>
                            <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">{record.portion}</td>
                        </tr>
                        ))
                    ) : (
                        <tr>
                        <td colSpan="7" className="py-4 px-4 text-center text-gray-500 dark:text-white">No Data Found</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* pagination */}
            {!isFiltering && monthlyRecords?.length > 0 && pagination.totalPages > 1 && (
                <ReactPaginate
                    breakLabel="..."
                    onPageChange={(event) => handlePageChange(event.selected + 1)}
                    pageRangeDisplayed={5}
                    pageCount={pagination.totalPages}
                    renderOnZeroPageCount={null}
                    nextLabel="Next >"
                    previousLabel="< Prev"
                    previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
                    nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                    containerClassName="flex items-center justify-center mt-4"
                    pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
                    activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
                />
            )}
        </div>
    </div>

  );
};

export default CheckAttendancePage;